import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Loader2, MailCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { readJsonOnce } from '../utils/safeFetch';
import { formatApiError } from '../utils/formatApiError';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await readJsonOnce(r);
      if (!r.ok) throw new Error(formatApiError(data && data.detail));
      setSent(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md relative z-10">
        <Button variant="ghost" className="mb-6 text-muted-foreground hover:text-foreground" onClick={() => navigate('/auth')} data-testid="forgot-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to sign in
        </Button>
        <div className="flex items-center mb-8">
          <img src="/ludy-wordmark.png" alt="Ludy" className="h-8" />
        </div>

        <Card className="bg-card border-border/50">
          {sent ? (
            <CardContent className="pt-8 pb-8 text-center space-y-4" data-testid="forgot-success">
              <MailCheck className="w-12 h-12 mx-auto text-primary" />
              <h1 className="font-heading text-2xl font-semibold text-foreground">Check your inbox</h1>
              <p className="text-sm text-muted-foreground">
                If an account exists with <span className="font-medium text-foreground">{email}</span>, we've sent a password reset link. It expires in 60 minutes.
              </p>
              <p className="text-xs text-muted-foreground">Didn't get it? Check your spam folder or try again in a few minutes.</p>
              <Button variant="outline" className="mt-2" onClick={() => navigate('/auth')} data-testid="forgot-done-btn">
                Return to sign in
              </Button>
            </CardContent>
          ) : (
            <>
              <CardHeader>
                <CardTitle className="font-heading text-2xl">Forgot your password?</CardTitle>
                <CardDescription>Enter the email you use for Ludy and we'll send you a link to reset it.</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={submit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="forgot-email"
                        type="email"
                        autoFocus
                        placeholder="you@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 bg-background border-border"
                        data-testid="forgot-email-input"
                      />
                    </div>
                  </div>
                  {error && <p className="text-sm text-destructive" data-testid="forgot-error">{error}</p>}
                  <Button type="submit" disabled={loading} className="w-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="forgot-submit">
                    {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : 'Send reset link'}
                  </Button>
                </form>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
